import { ListingMatch } from '@deltek/specification-client/core/Models/listingSearch'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { ProductTypeWithBODItem } from 'api/graphQL/types/projectElement.types'
import clsx from 'clsx'
import React from 'react'
import AutoSizer, { Size } from 'react-virtualized-auto-sizer'
import { FixedSizeGrid } from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'
import { ProductListingResultGridItem } from './ProductListingResultGridItem'

const useStyles = makeStyles((theme: Theme) => ({
    baseResultContainer: {
        flex: 1,
        paddingBottom: theme.spacing(2),
    },
    resultContainer: {
        width: '100%',
        margin: 0,
        overflow: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'none',
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
}))

export interface ProductListingResultGridProps {
    currentTotal: number
    searchLoading?: boolean
    searchFetchMoreLoading?: boolean
    products?: Partial<ListingMatch>[]
    handleLoadMore: (
        startIndex: number,
        stopIndex: number
    ) => Promise<any> | null
    hasNextPage: boolean
    isItemLoaded: (index: number) => boolean
    scrollState: {
        rowIndex: number
        columnIndex: number
    }
    setScrollRowAndColumn: (rowIndex: number, columnIndex: number) => void
    infiniteLoaderRef: React.Ref<any>
    setColumnCount: (value: number) => void
    isAssembly: boolean
    productTypesWithBOD: ProductTypeWithBODItem[]
}

// row height and column width takes into consideration the padding within the grid item
export const ROW_HEIGHT = 420
export const COLUMN_WIDTH = 370
export const PADDING = 24

export const ProductListingResultGrid: React.FC<ProductListingResultGridProps> = ({
    currentTotal,
    searchLoading,
    searchFetchMoreLoading,
    products = [],
    handleLoadMore,
    isItemLoaded,
    scrollState,
    setScrollRowAndColumn,
    hasNextPage,
    infiniteLoaderRef,
    setColumnCount,
    isAssembly,
    productTypesWithBOD,
}) => {
    const classes = useStyles()

    // this is a FixedSizeGrid ref to maintain scroll position but set to any to avoid complex typing
    const gridRef = React.useRef<FixedSizeGrid | null>(null)
    const previousProductsLength = React.useRef(products.length)

    const itemCount = hasNextPage ? products.length + 1 : products.length

    const handleResize = ({ width }: Size) => {
        setColumnCount(Math.floor(width / COLUMN_WIDTH))
    }

    // Restore scroll position after loading more items
    React.useEffect(() => {
        // Only restore scroll if we actually loaded more items
        if (products.length > previousProductsLength.current && gridRef.current) {
            // Use scrollToItem to maintain scroll position after loading more
            gridRef.current.scrollToItem({
                align: 'start',
                rowIndex: scrollState.rowIndex,
                columnIndex: scrollState.columnIndex,
            })
        }
        previousProductsLength.current = products.length
    }, [products.length, scrollState.rowIndex, scrollState.columnIndex])

    return (
        <>
            {!searchLoading && !searchFetchMoreLoading && products.length === 0 ? (
                <GridContainer
                    spacing={1}
                    className={clsx(
                        'divProductListingNoResultGrid',
                        classes.baseResultContainer,
                        classes.resultContainer
                    )}
                    justifyContent="center"
                    alignContent="space-between"
                >
                    <GridItem>
                        <h3>No Results</h3>
                    </GridItem>
                </GridContainer>
            ) : (
                <div
                    className={classes.baseResultContainer}
                    data-testid="productListingResultGrid"
                >
                    <AutoSizer
                        className="divProductListingResultGrid"
                        onResize={handleResize}
                        defaultWidth={400}
                        defaultHeight={450}
                    >
                        {({ width, height }) => {
                            const gridHeight = height
                            const gridWidth = width

                            // Divide the width with the default column width to get the number of columns
                            const columnCount = Math.floor(
                                gridWidth / COLUMN_WIDTH
                            )

                            // Based on the columnCount
                            const calculatedGridWidth =
                                columnCount * COLUMN_WIDTH

                            // Get the excess width
                            const excessWidth =
                                gridWidth - calculatedGridWidth - PADDING //padding on the right side
                            const excessWidthPerColumn =
                                excessWidth / columnCount

                            // Add excess to default column width
                            const newColumnWidth =
                                COLUMN_WIDTH +
                                excessWidthPerColumn -
                                PADDING / columnCount // distribute left padding to each item

                            const newColumnHeight =
                                ROW_HEIGHT - PADDING / columnCount // distribute top padding to each item

                            // Divide the count with column count to get the items count or the row count
                            const listItemCount = Math.ceil(
                                currentTotal / columnCount
                            )

                            return (
                                <InfiniteLoader
                                    ref={infiniteLoaderRef}
                                    isItemLoaded={isItemLoaded}
                                    itemCount={listItemCount}
                                    loadMoreItems={handleLoadMore}
                                    // get threshold based on the items rendered
                                    threshold={(itemCount / columnCount) * 0.6}
                                >
                                    {({ onItemsRendered, ref }) => (
                                        <FixedSizeGrid
                                            height={gridHeight}
                                            width={gridWidth}
                                            rowHeight={newColumnHeight}
                                            columnWidth={newColumnWidth}
                                            rowCount={listItemCount}
                                            columnCount={columnCount}
                                            initialScrollTop={
                                                ROW_HEIGHT *
                                                scrollState.rowIndex
                                            }
                                            onItemsRendered={({
                                                visibleRowStartIndex,
                                                visibleColumnStartIndex,
                                                visibleRowStopIndex,
                                                overscanRowStopIndex,
                                                overscanRowStartIndex,
                                            }) => {
                                                // Update scroll state when user scrolls
                                                setScrollRowAndColumn(visibleRowStartIndex, visibleColumnStartIndex)

                                                onItemsRendered({
                                                    overscanStartIndex: overscanRowStartIndex,
                                                    overscanStopIndex: overscanRowStopIndex,
                                                    visibleStartIndex: visibleRowStartIndex,
                                                    visibleStopIndex: visibleRowStopIndex,
                                                })
                                            }}
                                            ref={(grid) => {
                                                // Set both refs - the InfiniteLoader ref and our local ref
                                                if (typeof ref === 'function') {
                                                    ref(grid)
                                                } else if (ref) {
                                                    (ref as any).current = grid
                                                }
                                                gridRef.current = grid
                                            }}
                                            innerElementType={(props) => {
                                                const style = {
                                                    ...props.style,
                                                    paddingTop: PADDING,
                                                    paddingLeft: PADDING,
                                                }
                                                return (
                                                    <div style={style}>
                                                        {props.children}
                                                    </div>
                                                )
                                            }}
                                        >
                                            {({
                                                columnIndex,
                                                rowIndex,
                                                ...rest
                                            }) => {
                                                const count =
                                                    rowIndex * columnCount +
                                                    columnIndex
                                                const match = products[count]
                                                return (
                                                    <ProductListingResultGridItem
                                                        {...rest}
                                                        match={match}
                                                        firstCard={count === 0}
                                                        loaded={isItemLoaded(
                                                            count
                                                        )}
                                                        loading={
                                                            searchFetchMoreLoading ||
                                                            searchLoading
                                                        }
                                                        isAssembly={isAssembly}
                                                        productTypesWithBOD={
                                                            productTypesWithBOD
                                                        }
                                                    />
                                                )
                                            }}
                                        </FixedSizeGrid>
                                    )}
                                </InfiniteLoader>
                            )
                        }}
                    </AutoSizer>
                </div>
            )}
        </>
    )
}
